import React, { useState, useCallback, useEffect } from 'react';
import './App.css';
import './themes.css';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import CardGrid from './components/CardGrid';
import Footer from './components/Footer';
import AddCardModal from './components/AddCardModal';
import EditCardModal from './components/EditCardModal';
import SearchSuggestions from './components/SearchSuggestions';
import { initToastSystem, setupToastListeners } from './components/ToastNotification';
import SettingsSidebar from './components/SettingsSidebar';
import StorageManager from './utils/StorageManager';
import BackgroundDoodles from './components/BackgroundDoodles';
import DuplicateCardCleaner from './utils/DuplicateCardCleaner';
import AnimatedPopup from './components/AnimatedPopup';
import giffile from './img4c.gif';

function App() {
  // Initialize state from StorageManager for settings
  const [searchTerm, setSearchTerm] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [isAddCardModalOpen, setIsAddCardModalOpen] = useState(false);
  const [isEditCardModalOpen, setIsEditCardModalOpen] = useState(false);
  const [currentCard, setCurrentCard] = useState(null);
  const [viewMode, setViewMode] = useState(StorageManager.getViewMode());
  const [showArchived, setShowArchived] = useState(StorageManager.getShowArchived());
  const [prefilledCard, setPrefilledCard] = useState(null);
  const [currentTheme, setCurrentTheme] = useState(StorageManager.getTheme());
  const [customThemeColors, setCustomThemeColors] = useState(StorageManager.getCustomThemeColors());
  const [isSettingsSidebarOpen, setIsSettingsSidebarOpen] = useState(false);
  const [cards, setCards] = useState([]);
  const [sortMethod, setSortMethod] = useState('newest'); 
  const [hiddenTags, setHiddenTags] = useState([]); // Track hidden tags
  const [hideCardsWithHiddenTags, setHideCardsWithHiddenTags] = useState(true);
  const [appLoaded] = useState(false);
  const [showPopup, setShowPopup] = useState(true);
  const [groupByCategory, setGroupByCategory] = useState(null);
  
  const [doodleSettings, setDoodleSettings] = useState({
    shapes: ['circle', 'triangle', 'star', 'square', 'hexagon', 'diamond', 'cross', 'zigzag', 'wave'],
    density: 20,
    colors: ['#ff5733', '#33ff57', '#3357ff'],
    minSize: 10,
    maxSize: 50,
    opacity: 0.4,
    strokeWidth: 2,
    animate: true,
    animationSpeed: 1,
    outlineOnly: true
  });
  
  const updateDoodleSettings = useCallback((newSettings) => {
    setDoodleSettings(newSettings);
    const success = StorageManager.saveDoodleSettings(newSettings);
    if (success) {
      setDoodleSettings(newSettings);
    }
  }, []);

  
  // Get methods from useCards hook - no need to destructure all methods if not used
  const allTags = React.useMemo(() => {
    // Extract all tags from all cards
    const tagSet = new Set();
    cards.forEach(card => {
      if (Array.isArray(card.tags)) {
        card.tags.forEach(tag => tagSet.add(tag));
      }
    });
    // Convert Set to sorted Array
    return Array.from(tagSet).sort();
  }, [cards]);

  useEffect(() => {
    initToastSystem({
      allowDuplicates: false,
      defaultDuration: 3000
    });
    setupToastListeners();
  }, []);

  useEffect(() => {
    const storedDoodleSettings = StorageManager.getDoodleSettings();
    if (storedDoodleSettings) {
      setDoodleSettings(storedDoodleSettings);
    }
  }, []);

  // Load cards from localStorage
  useEffect(() => {
    const storedCards = StorageManager.getCards();
    setCards(storedCards);
  }, []);

  // Apply theme to document
  useEffect(() => {
    document.body.className = currentTheme;
    
    // Apply custom theme if selected
    if (currentTheme === 'custom-theme' && customThemeColors) {
      const root = document.documentElement;
      
      for (const [key, value] of Object.entries(customThemeColors)) {
        let cssVarName;
        switch (key) {
          case 'primary':
            cssVarName = '--primary';
            // Also set the hover color (slightly darker)
            root.style.setProperty('--primary-hover', adjustColor(value, -20));
            break;
          case 'background':
            cssVarName = '--background';
            break;
          case 'cardBg':
            cssVarName = '--card-bg';
            break;
          case 'textPrimary':
            cssVarName = '--text-primary';
            break;
          case 'textSecondary':
            cssVarName = '--text-secondary';
            break;
          case 'border':
            cssVarName = '--border';
            break;
          default:
            cssVarName = `--${key}`;
        }
        
        root.style.setProperty(cssVarName, value);
      }
    }
  }, [currentTheme, customThemeColors]);

  // Helper function to adjust color brightness
  const adjustColor = (color, amount) => {
    return '#' + color.replace(/^#/, '').replace(/../g, color => 
      ('0' + Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)).slice(-2)
    );
  };

  // Toggle settings sidebar visibility
  const toggleSettingsSidebar = useCallback(() => {
    console.log("Toggling settings sidebar, current state:", isSettingsSidebarOpen);
    setIsSettingsSidebarOpen(prev => !prev);
  }, [isSettingsSidebarOpen]);
  
  const resetAllSettings = useCallback(() => {
    const success = StorageManager.resetAllSettings();
    if (success) {
      setViewMode('grid');
      setCurrentTheme('light-theme');
      setCustomThemeColors(null);
      setShowArchived(false);
      // Force reload to apply all changes
      setTimeout(() => window.location.reload(), 1000);
    }
  }, []);

  const getCategoryGroupLabel = (groupBy) => {
    switch(groupBy) {
      case 'firstLetter':
        return 'Group by First Letter';
      case 'tag':
        return 'Group by First Tag';
      default:
        return 'Group by ' + groupBy;
    }
  };


  // Add this function to your App component
const checkAndCleanDuplicates = useCallback(() => {
  const { removedDuplicates, hasDuplicates } = DuplicateCardCleaner.cleanupDuplicates(
    cards, 
    (cleanedCards) => {
      // Save cleaned cards to storage
      StorageManager.saveCards(cleanedCards, false);
      // Update state with cleaned cards
      setCards(cleanedCards);
    }
  );
  
  if (hasDuplicates) {
    window.toast.error(`Removed ${removedDuplicates.length} duplicate card${removedDuplicates.length > 1 ? 's' : ''}`);
  }
  
  return hasDuplicates;
  }, [cards]);

  useEffect(() => {
    checkAndCleanDuplicates();
  }, [cards, checkAndCleanDuplicates]);
  
  const exportSettings = useCallback(() => {
    const settings = StorageManager.exportSettings();
    if (settings) {
      const dataStr = JSON.stringify(settings, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      const exportFileName = 'card-manager-settings.json';
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileName);
      linkElement.click();
    }
  }, []);
  
  const importSettings = useCallback((fileContent) => {
    const success = StorageManager.importSettings(fileContent);
    if (success) {
      // Reload the page to apply all changes
      setTimeout(() => window.location.reload(), 1000);
    }
    return success;
  }, []);

  // Handle view mode change
  const handleViewModeChange = useCallback((mode) => {
    StorageManager.saveViewMode(mode);
    setViewMode(mode);
  }, []);

  const sortCards = (method) => {
    setSortMethod(method);
    
    // Create a copy of the cards array to sort
    const sortedCards = [...cards];
    
    // Sort based on the selected method
    switch (method) {
      case 'newest':
        sortedCards.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case 'oldest':
        sortedCards.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
      case 'alphabetical':
        sortedCards.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'reverseAlphabetical':
        sortedCards.sort((a, b) => b.title.localeCompare(a.title));
        break;
      default:
        // Default to newest first
        sortedCards.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }
    
    setCards(sortedCards);
  };

  // Add this function to toggle tag visibility
  const toggleTagVisibility = (tag) => {
    if (hiddenTags.includes(tag)) {
      setHiddenTags(hiddenTags.filter(t => t !== tag));
    } else {
      setHiddenTags([...hiddenTags, tag]);
    }
  };
  
  // Add this function to toggle whether to hide cards with hidden tags
  const toggleHideCardsWithHiddenTags = () => {
    setHideCardsWithHiddenTags(!hideCardsWithHiddenTags);
  };
  
  // Function to filter cards based on visibility settings
  const getVisibleCards = () => {
    // Start with the base cards
    let visibleCards = [...cards];
    
    // Apply tag filter if a specific tag is selected
    if (selectedTag !== null) {
      visibleCards = visibleCards.filter(card => card.tags.includes(selectedTag));
    }
    
    // Apply archive filter - using isArchived property
    visibleCards = visibleCards.filter(card => {
      // If showArchived is true, only show archived cards
      // If showArchived is false, only show non-archived cards
      return showArchived ? Boolean(card.isArchived) : !Boolean(card.isArchived);
    });
    
    // Apply hidden tags filter if enabled
    if (hideCardsWithHiddenTags && hiddenTags.length > 0) {
      visibleCards = visibleCards.filter(card => {
        // A card is visible if it doesn't have any hidden tags
        return !card.tags.some(tag => hiddenTags.includes(tag));
      });
    }
    
    return visibleCards;
  };
  
  
  // Handle theme change
  const handleThemeChange = useCallback((themeId, customColors = null) => {
    StorageManager.saveTheme(themeId);
    setCurrentTheme(themeId);
    
    if (themeId === 'custom-theme' && customColors) {
      StorageManager.saveCustomThemeColors(customColors);
      setCustomThemeColors(customColors);
    }
  }, []);

  const enhancedAddCard = useCallback((cardData) => {
    try {
      const newCard = StorageManager.addCard(cardData);
      if (newCard) {
        setCards(prev => [...prev, newCard]);
        // StorageManager.addCard already shows a toast
        return newCard;
      }
    } catch (error) {
      console.error('Error adding card:', error);
    }
    return null;
  }, []);

  

  const enhancedUpdateCard = useCallback((id, cardData) => {
    try {
      const success = StorageManager.updateCard(id, cardData);
      if (success) {
        setCards(prev => prev.map(card => card.id === id ? {...card, ...cardData} : card));
        window.toast.success('Card updated successfully');
        return true;
      }
    } catch (error) {
      window.toast.error('Failed to update card');
      console.error('Error updating card:', error);
    }
    return false;
  }, []);

  const enhancedArchiveCard = useCallback((id) => {
    try {
      // Find the card to determine current archive status
      const card = cards.find(card => card.id === id);
      if (!card) return false;
      
      // Toggle the archive status
      const newArchiveStatus = !card.isArchived;
      const success = StorageManager.archiveCard(id, newArchiveStatus);
      if (success) {
        setCards(prev => prev.map(card => 
          card.id === id ? {...card, isArchived: newArchiveStatus} : card
        ));
        return true;
      }
    } catch (error) {
      console.error('Error archiving/restoring card:', error);
    }
    return false;
  }, [cards]);

  const enhancedDeleteCard = useCallback((id) => {
    try {
      const success = StorageManager.deleteCard(id);
      if (success) {
        setCards(prev => prev.filter(card => card.id !== id));
        window.toast.info('Card deleted');
        return true;
      }
    } catch (error) {
      window.toast.error('Failed to delete card');
      console.error('Error deleting card:', error);
    }
    return false;
  }, []);

  const enhancedDuplicateCard = useCallback((id) => {
    try {
      const newCard = StorageManager.duplicateCard(id);
      if (newCard) {
        setCards(prev => [...prev, newCard]);
        window.toast.success('Card duplicated successfully');
        return newCard;
      }
    } catch (error) {
      window.toast.error('Failed to duplicate card');
      console.error('Error duplicating card:', error);
    }
    return null;
  }, []);

  const enhancedExportCards = useCallback(() => {
    try {
      const dataStr = JSON.stringify(cards, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      const exportFileName = 'card-manager-data.json';
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileName);
      linkElement.click();
      
      window.toast.success('Cards exported successfully');
      return true;
    } catch (error) {
      window.toast.error('Failed to export cards');
      console.error('Error exporting cards:', error);
      return false;
    }
  }, [cards]);

  const enhancedImportCards = useCallback((data) => {
    try {
      // Parse the data if it's a string
      const jsonData = typeof data === 'string' ? JSON.parse(data) : data;
      
      // If it's a template format with a 'cards' array
      if (jsonData.cards && Array.isArray(jsonData.cards)) {
        StorageManager.importFromTemplate(jsonData);
        return;
      }
      
      // If it's a direct array of cards
      if (Array.isArray(jsonData)) {
        const currentCards = StorageManager.getCards();
        
        // Process new cards
        const newCards = jsonData.map(card => ({
          ...card,
          id: card.id,
          createdAt: card.createdAt || new Date().toISOString(),
        }));
        
        // Combine and save
        const updatedCards = [...currentCards, ...newCards];
        StorageManager.saveCards(updatedCards, true, `Imported ${newCards.length} cards`);
        return;
      }
      
      throw new Error('Invalid import format');
    } catch (error) {
      console.error('Import error:', error);
      window.toast?.error('Failed to import: Invalid format');
    }
  }, []);

  // Handle search term change
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  // Effect to prefill the Add Card modal when opened via search
  useEffect(() => {
    if (isAddCardModalOpen && prefilledCard) {
      setCurrentCard(prefilledCard);
    }
  }, [isAddCardModalOpen, prefilledCard]);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen(prev => !prev);
  }, []);

  const filterByTag = useCallback((tag) => {
    setSelectedTag(prevTag => prevTag === tag ? null : tag);
  }, []);

  const openEditModal = useCallback((card) => {
    setCurrentCard({
      ...card,
      customTag: card.tags.join(', '),
      iconUrl: card.icon?.type === 'image' ? card.icon.url : ''
    });
    setIsEditCardModalOpen(true);
  }, []);

  

  const closeAddModal = useCallback(() => {
    setIsAddCardModalOpen(false);
    setPrefilledCard(null);
    setSearchTerm(''); 
  }, []);

  const closeEditModal = useCallback(() => {
    setIsEditCardModalOpen(false);
    setCurrentCard(null);
  }, []);

  // Open Add Card Modal with Search Term
  const handleOpenAddModalWithTerm = useCallback(() => {
    setPrefilledCard({
      title: searchTerm || '',
      description: '',
      tags: [],
      icon: null,
      url: '',
      customTag: ''
    });
    setIsAddCardModalOpen(true);
  }, [searchTerm]);

  // Open Add Card Modal with Prefilled Data
  const openAddModal = useCallback((prefillData = null) => {
    setPrefilledCard(prefillData ? {
      title: prefillData.title || '',
      description: prefillData.description || '',
      tags: prefillData.tags || [],
      icon: prefillData.icon || null,
      url: prefillData.url || '',
      customTag: prefillData.tags ? prefillData.tags.join(', ') : ''
    } : null);
    
    setIsAddCardModalOpen(true);
    setSearchTerm(''); // Clear search term when adding from suggestion
  }, []);


  // Filter Cards
  const filteredCards = getVisibleCards();

  // Count for Footer
  const activeCount = cards.filter(card => !card.isArchived).length;
  const archivedCount = cards.filter(card => card.isArchived).length;

  const toggleArchivedView = useCallback(() => {
    const newValue = !showArchived;
    StorageManager.saveShowArchived(newValue);
    setShowArchived(newValue);
  }, [showArchived]);

  // Show suggestions if no matching cards
  const showSuggestions = searchTerm && filteredCards.length === 0;

  return (
    <div className={`app ${currentTheme}`}>
       {showPopup && (
        <AnimatedPopup 
          gifUrl={giffile}
          messages={["Getting Card sturctures", "Laying the frames", "Fetching the backend", "Getting things ready.."]}
          onClose={() => setShowPopup(false)}
          displayDuration={4500}
        />
      )}
      
      <div className={`app-content ${appLoaded ? 'fade-in' : 'hidden'}`}></div>
      <Header 
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        toggleSidebar={toggleSidebar}
        openAddModal={handleOpenAddModalWithTerm}
        exportCards={enhancedExportCards}
        importCards={enhancedImportCards}
        viewMode={viewMode}
        toggleSettingsSidebar={toggleSettingsSidebar}
        setViewMode={handleViewModeChange}
        setCards={setCards}
        filterByTag={filterByTag}
        selectedTag={selectedTag}
        allTags={allTags}
        cards={cards}
        groupByCategory={groupByCategory}
        setGroupByCategory={setGroupByCategory}
        getCategoryGroupLabel={getCategoryGroupLabel}
      />

      <BackgroundDoodles 
        shapes={doodleSettings.shapes}
        density={doodleSettings.density}
        colors={doodleSettings.colors}
        minSize={doodleSettings.minSize}
        maxSize={doodleSettings.maxSize}
        opacity={doodleSettings.opacity}
        strokeWidth={doodleSettings.strokeWidth}
        animate={doodleSettings.animate}
        outlineOnly={doodleSettings.outlineOnly}
        animationSpeed={doodleSettings.animationSpeed}
      />

      <Sidebar 
        isOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        allTags={allTags}
        selectedTag={selectedTag}
        filterByTag={filterByTag}
        toggleArchivedView={toggleArchivedView}
        showArchived={showArchived}
        sortCards={sortCards}
        currentSortMethod={sortMethod}
        hiddenTags={hiddenTags}
        toggleTagVisibility={toggleTagVisibility}
        hideCardsWithHiddenTags={hideCardsWithHiddenTags}
        toggleHideCardsWithHiddenTags={toggleHideCardsWithHiddenTags}
        cards={cards}
      />

      

      <main className={`cards-container ${isSidebarOpen ? 'with-sidebar' : ''}`}>
        {filteredCards.length > 0 ? (
          <CardGrid 
          cards={filteredCards}
          viewMode={viewMode}
          onEdit={openEditModal}
          onArchive={enhancedArchiveCard}
          onDelete={enhancedDeleteCard}
          onDuplicate={enhancedDuplicateCard}
          groupByCategory={groupByCategory}
          />
        ) : (
          <div className="no-results">
            {showSuggestions ? (
              <SearchSuggestions 
                searchTerm={searchTerm}
                onAddCard={openAddModal}
              />
            ) : (
              <p>No cards found. {searchTerm && 'Try a different search term or '} 
                {selectedTag && 'select a different tag or '} 
                <button onClick={() => setIsAddCardModalOpen(true)} className="text-button" style={{ display: 'inline' }}>
                  add a card ?
                </button>
              </p>
            )}
          </div>
        )}
      </main>

      <Footer 
        activeCount={activeCount} 
        archivedCount={archivedCount} 
        totalCount={cards.length} 
        showArchived={showArchived}
        toggleArchivedView={toggleArchivedView}
      />


      {isAddCardModalOpen && (
        <AddCardModal 
          onClose={closeAddModal}
          onAddCard={enhancedAddCard}
          prefillData={prefilledCard}
        />
      )}

      {isEditCardModalOpen && currentCard && (
        <EditCardModal 
          card={currentCard}
          onClose={closeEditModal}
          onUpdateCard={enhancedUpdateCard}
        />
      )}
      
      {/* Added debugging info and made sure this always renders when isSettingsSidebarOpen is true */}
      {isSettingsSidebarOpen && (
        <SettingsSidebar
          isOpen={isSettingsSidebarOpen}
          onClose={toggleSettingsSidebar}
          currentTheme={currentTheme}
          onThemeChange={handleThemeChange}
          viewMode={viewMode}
          setViewMode={handleViewModeChange}
          showArchived={showArchived}
          toggleArchivedView={toggleArchivedView}
          resetAllSettings={resetAllSettings}
          customThemeColors={customThemeColors}
          exportSettings={exportSettings}
          importSettings={importSettings}
          doodleSettings={doodleSettings}
          updateDoodleSettings={updateDoodleSettings}
        />
      )}
    </div>
  );
}

export default App;